import {
	Listbox,
	ListboxButton,
	ListboxOption,
	ListboxOptions,
	ListboxOptionsProps,
	ListboxProps,
	Transition,
} from "@headlessui/react";
import { ElementType, Fragment, useMemo, useRef } from "react";
import { useLabelledBy } from "../../lib/useLabelledBy";
import { cn } from "../../lib/utils";
import { Icon } from "../../primitives";
import { inputSize } from "./inputSize";

export type DropdownInputProps<
	TType = string,
	TTag extends ElementType = "div",
	TActualType = TType extends (infer U)[] ? U : TType,
> = ListboxProps<TTag, TType, TActualType> & {
	testId?: string;
	buttonClassName?: string;
	buttonId?: string;
	"aria-labelledby"?: string;
	buttonIconClassName?: string;
	optionsClassName?: string;
	optionClassName?: string;
	optionIconClassName?: string;
	placeholder?: string;
	compact?: boolean;
	options: {
		label: string;
		value: string;
		className?: string;
	}[];
	disabledOptions?: string[];
	error?: boolean;
	readonly?: boolean;
	className?: string;
	deselectable?: boolean;
	// When set, options are portaled and floated relative to the button so they
	// aren't clipped by scrollable/overflow-hidden ancestors (e.g. inside a modal).
	anchor?: ListboxOptionsProps["anchor"];
};

export const DropdownInput = <TType extends string>({
	testId,
	buttonClassName,
	buttonId,
	"aria-labelledby": ariaLabelledBy,
	buttonIconClassName,
	optionsClassName,
	optionClassName,
	optionIconClassName,
	placeholder,
	compact,
	options,
	error = false,
	disabled,
	readonly,
	className,
	deselectable,
	disabledOptions,
	anchor,
	...props
}: DropdownInputProps<TType>) => {
	const controlledValue = (props.value === undefined ? "" : props.value) as TType;
	const listboxRef = useRef<HTMLButtonElement>(null);
	const buttonRef = useLabelledBy<HTMLButtonElement>(ariaLabelledBy);
	const { frame, paddingX, text } = inputSize(compact);

	const selectedOption = useMemo(() => {
		if (controlledValue !== "" && controlledValue !== null && controlledValue !== undefined) {
			return options.find((option) => option.value === controlledValue);
		}
		return null;
	}, [controlledValue, options]);

	const handleChange = (value: TType) => {
		if (deselectable && value === props.value) {
			props.onChange?.(null as unknown as TType);
		} else {
			props.onChange?.(value);
		}
	};

	return (
		<Listbox
			{...props}
			onChange={handleChange}
			value={controlledValue}
			as="div"
			className={cn("relative h-fit", className)}
			disabled={options.length === 0 || disabled || readonly}
			ref={listboxRef}
		>
			<ListboxButton
				id={buttonId}
				ref={buttonRef}
				data-testid={testId || "dropdown-button"}
				type="button"
				tabIndex={0}
				className={cn(
					"group flex items-center w-full text-left space-x-[3px] text-primary cursor-pointer rounded-md bg-surface-light/70 outline outline-[1px] outline-offset-[-1px] outline-bc-strong focus-visible:outline-text-secondary focus-visible:hover:outline-text-secondary focus-visible:outline-[2px] data-[headlessui-state=open]:outline-text-secondary disabled:cursor-default data-[headlessui-state=open]:outline-[2px]",
					frame,
					paddingX,
					disabled ? "cursor-not-allowed opacity-50" : "hover:outline-text-tertiary",
					readonly ? "bg-surface-neutral/70 opacity-100 outline-[0px]" : undefined,
					error &&
						"outline-danger hover:outline-danger focus-visible:outline-danger data-[headlessui-state=open]:outline-danger",
					buttonClassName,
				)}
			>
				{selectedOption ? (
					<div className="relative w-full flex-1">
						<div>{selectedOption.label}</div>
					</div>
				) : (
					<div className="flex-1 text-secondary group-disabled:text-tertiary">{placeholder}</div>
				)}
				<Icon
					type="chevron-down"
					className={cn("text-[16px] text-tertiary group-disabled:opacity-70", buttonIconClassName)}
				/>
			</ListboxButton>
			<Transition
				as={Fragment}
				leave="transition ease-in duration-75"
				leaveFrom="opacity-100"
				leaveTo="opacity-0"
			>
				<ListboxOptions
					anchor={anchor}
					className={cn(
						"overflow-y-auto w-fit px-0 text-secondary font-normal rounded bg-surface-base shadow-lg cursor-pointer select-none outline outline-[1px] outline-bc-default outline-offset-[-1px]",
						text,
						anchor
							? "min-w-[var(--button-width)] mt-0 z-[200001] [--anchor-gap:0.25rem]"
							: "absolute min-w-full mt-1 z-[5000]",
						optionsClassName,
					)}
				>
					<div className="w-full h-fit">
						{options.map((option, index) => {
							const isDisabled = disabledOptions?.includes(option.value);
							return (
								<ListboxOption
									data-testid="dropdown-option"
									key={index}
									className={cn(
										"flex items-center text-primary w-full py-[6px] space-x-[10px] data-[active]:bg-surface-neutral",
										paddingX,
										"pr-[10px]",
										optionClassName,
										option.className,
										isDisabled && "bg-surface-neutral/80 opacity-50 cursor-not-allowed",
									)}
									value={option.value}
									disabled={isDisabled}
								>
									{() => (
										<>
											<div className="h-fit flex-1">{option.label}</div>
											{controlledValue === option.value ? (
												<Icon
													type="check"
													filled
													className={cn("text-secondary text-[16px]", optionIconClassName)}
												/>
											) : (
												<div className="w-[16px] h-[16px]" />
											)}
										</>
									)}
								</ListboxOption>
							);
						})}
					</div>
				</ListboxOptions>
			</Transition>
		</Listbox>
	);
};
